import { BlockBlobClient } from '@azure/storage-blob';
import { TAG_KEYS, TAG_NAMING } from '@components/imageHandling/common';
import { compareOrder, isWeb } from '@helpers';
import {
  ImageContext,
  ImageDocumentStoreItem,
  ODataOrRetrievedDocumentLink,
  RetrievedDocumentReferenceLink,
  GenerateGropedImagesAndKeysResult,
  GroupedImage,
  GroupedImageItem,
  IsSomeLinkFavoriteForScopeParams,
} from '@v2/components/imageHandling/types/types.d';
import { ImageData } from '@v2/context/Image/context/interfaces';
import { ScopeId } from '@interfaces/base';
import i18n from 'i18n-js';
import ReactNativeBlobUtil from 'react-native-blob-util';

const IMAGE_CONTEXT_DELIMITER = '_';

export const getImageContextKeyForMultipleImages = ({ scopeId, scopeReferenceId }: { scopeId: keyof typeof TAG_KEYS; scopeReferenceId: number }) => {
  if (scopeId == null || scopeReferenceId == null) return '';
  return `${scopeId}${IMAGE_CONTEXT_DELIMITER}${scopeReferenceId}`;
};

export const getImageContextKey = (params: ImageContext): string => {
  if (!params) {
    return '';
  }

  if ('documentId' in params) {
    return String(params.documentId);
  }
  if ('scopeReferenceId' in params && 'scopeId' in params) {
    return getImageContextKeyForMultipleImages(params);
  }

  return '';
};

export const isSomeLinkFavoriteForScope = ({ links, scopeId, scopeReferenceId }: IsSomeLinkFavoriteForScopeParams): boolean => {
  if (scopeId == null || scopeReferenceId == null) return false;
  return !!links?.some((l) => l.scopeId === scopeId && l.scopeReferenceId === scopeReferenceId && l.isFavorite);
};

export const buildImageDataFromDocument = (doc: ImageDocumentStoreItem, scopeId: number | undefined): ImageData => {
  const pageNumber = doc.pageNumber ?? 1;
  const pageDetail = doc.pageDetails?.find((p) => p.pageNumber === pageNumber);
  const cropSetting = doc.cropSettings?.find((cs) => cs.pageNumber === pageNumber);
  return {
    isCropped: !!doc.isCropped,
    extensionDerivative: doc.extensionDerivative ?? '',
    pageNumber,
    cropSettings: doc.cropSettings ?? undefined,
    originalWidth: pageDetail?.width,
    originalHeight: pageDetail?.height ?? 0,
    rotationAngle: pageDetail?.rotationAngle,
    rotationAngleCrop: cropSetting?.rotationAngle,
    scopeId: scopeId as ScopeId | undefined,
  };
};

export const getImageContextFromContextKey = (contextKey: string | null): ImageContext => {
  if (contextKey == null) return {};

  if (contextKey.includes(IMAGE_CONTEXT_DELIMITER)) {
    const [scopeId, scopeReferenceId] = contextKey.split(IMAGE_CONTEXT_DELIMITER);
    return { scopeId: Number(scopeId), scopeReferenceId: Number(scopeReferenceId) };
  } else {
    return { documentId: Number(contextKey) };
  }
};

export const formatLinkLabel = (link: RetrievedDocumentReferenceLink): { categoryName: string; referenceName: string } => {
  if (!link || !link.scopeId)
    return {
      categoryName: '',
      referenceName: '',
    };

  const categoryKey = TAG_KEYS[link.scopeId as keyof typeof TAG_KEYS];

  const categoryName = categoryKey ? TAG_NAMING[categoryKey] : 'Unknown';
  const referenceId = link.scopeReferenceName || link.scopeReferenceId || '';

  return {
    categoryName: i18n.t(categoryName),
    referenceName: String(referenceId),
  };
};

export const generateGropedImagesAndKeys = (imagesList: ImageDocumentStoreItem[]): GenerateGropedImagesAndKeysResult => {
  if (!imagesList || !imagesList.length) {
    return { groupedImages: {}, groupedImagesKeys: [] };
  }

  const images = [...imagesList];

  const groupedImages: Record<string, GroupedImage> = {};

  const indexID: Record<number, number> = {};

  images.forEach((i, index) => {
    let amountOfFavorite = 0;
    let lastLink: ODataOrRetrievedDocumentLink = i?.links?.[0] || {};
    i?.links?.forEach((l) => {
      if (lastLink.scopeId < l.scopeId) {
        lastLink = l;
      }
      if (l.isFavorite) {
        amountOfFavorite += 1;
      }
    });

    const key = `${TAG_KEYS[lastLink.scopeId as keyof typeof TAG_KEYS]}-${lastLink.scopeReferenceId}`;
    if (!groupedImages[key]) {
      groupedImages[key] = {
        link: lastLink,
        images: [],
        scopeId: lastLink.scopeId,
        key,
      };
    }
    groupedImages[key].images.push(Object.assign({ index, imageLinkKey: key, isFavorite: lastLink.isFavorite }, i) as GroupedImageItem);
  });

  const groupedImagesKeys = Object.keys(groupedImages).sort(compareOrder);

  images.forEach((i, index) => {
    indexID[i.id] = index;
  });

  Object.values(groupedImages).forEach((g: GroupedImage) => {
    g.images = g.images.map((i) => ({ ...i, index: indexID[i.id] })).reverse();
  });

  return {
    groupedImages,
    groupedImagesKeys: groupedImagesKeys.reverse(),
  };
};

export const isTempID = (id: number): boolean => String(id).includes('TEMPIMAGEID-');

export const shouldSwapDimensions = (rotationAngle: number): boolean => {
  return rotationAngle === 90 || rotationAngle === 270;
};

export const convertAngle = (a: number): number => {
  if (a < 0) {
    return 360 + a;
  }
  if (a >= 360) {
    return convertAngle(a - 360);
  }
  return a;
};

export const getAngle = (image: ImageDocumentStoreItem): number => {
  const customRotateAngle = image?.customRotate?.[image.pageNumber] || 0;
  const angle = image.isCropped
    ? (image.cropSettings?.find((cs) => cs.pageNumber === image.pageNumber)?.rotationAngle || 0) + customRotateAngle
    : customRotateAngle;
  return angle % 360;
};

export const updateFileInBlobStorage = async ({
  blobBaseUrl,
  sasToken,
  file,
  content,
}: {
  blobBaseUrl: string;
  sasToken: string;
  file: Blob;
  content: string;
}) => {
  const fullBlobUrl = `${blobBaseUrl}?${sasToken}`;

  if (isWeb) {
    const blobClient = new BlockBlobClient(fullBlobUrl);
    const options = { blobHTTPHeaders: { blobContentType: file.type } };

    await blobClient.uploadData(file, options);
  } else {
    const base64Content = ReactNativeBlobUtil.base64.encode(content);

    await ReactNativeBlobUtil.fetch(
      'PUT',
      fullBlobUrl,
      {
        'x-ms-blob-type': 'BlockBlob',
        'content-type': 'application/octet-stream',
        'x-ms-blob-content-type': file.type,
        ignoreInterceptor: 'true',
      },
      base64Content
    );
  }
};
